/**
 * Klein uitklapmenu aan een eigen knop (UX-uitrol 20 oktober 2026): het ⋯-menu van een open stap (D6) en
 * van een open vraag (Q5), en het niveaumenu van een stap of tabblad (D4). Zelfde opmaak als het ⋯-menu in
 * de titelbalk (pane-header__menu). Sluit met Esc (focus terug op de knop), een klik erbuiten of na een keuze.
 *
 *   <MoreMenu label="Meer" items={[{ label: "Vraag toevoegen", icon, onClick }, { label: "Antwoord wissen",
 *             danger: true, confirm: { label: "Wissen", cancel: "Annuleren" }, onClick }]} />
 *
 * Een regel met `confirm` vraagt eerst om bevestiging in het menu zelf (alert() werkt niet in een add-in).
 * `trigger` vervangt de standaard ⋯-knop (bijvoorbeeld de niveauknop); `head` en `foot` zijn korte
 * uitlegregels boven en onder de regels.
 */

import * as React from "react";
import "./pane.css";
import { useKeepInPane } from "./useKeepInPane";

export interface MoreMenuItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  title?: string;
  disabled?: boolean;
  /** Vinkje links (instelling of gekozen stand). */
  checked?: boolean;
  /** Rode tekst (verwijderen, wissen). */
  danger?: boolean;
  /** Eerst bevestigen, in het menu zelf. */
  confirm?: { label: string; cancel: string };
  /** Scheidingslijn boven deze regel. */
  separator?: boolean;
}

export interface MoreMenuProps {
  items: MoreMenuItem[];
  /** Toegankelijke naam en tooltip van de standaardknop. */
  label: string;
  /** Eigen knop in plaats van ⋯; krijgt open-stand via className "is-open". */
  trigger?: (open: boolean) => React.ReactNode;
  head?: string;
  foot?: string;
  align?: "left" | "right";
  className?: string;
  /** Menu meldt dat het open of dicht gaat (de kaart houdt zich dan gemount, PERF-04). */
  onOpenChange?: (open: boolean) => void;
}

export function MoreMenu({
  items,
  label,
  trigger,
  head,
  foot,
  align = "right",
  className,
  onOpenChange,
}: MoreMenuProps): JSX.Element {
  const [open, setOpenState] = React.useState(false);
  const [confirming, setConfirming] = React.useState<number | null>(null);
  const rootRef = React.useRef<HTMLSpanElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const setTriggerRef = (el: HTMLElement | null): void => {
    triggerRef.current = el;
  };
  // Binnen de pane houden, ook op 320 px (ST14, #2752); opnieuw bij een bevestigingsregel.
  useKeepInPane(menuRef, open, confirming);
  const setOpen = (v: boolean): void => {
    setOpenState(v);
    if (!v) setConfirming(null);
    onOpenChange?.(v);
  };

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choose = (item: MoreMenuItem, i: number): void => {
    if (item.disabled) return;
    if (item.confirm && confirming !== i) {
      setConfirming(i);
      return;
    }
    setOpen(false);
    item.onClick();
  };

  return (
    <span
      className={`pane-dropmenu${className ? ` ${className}` : ""}`}
      ref={rootRef}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // Toetsen blijven in het menu (de kaart eromheen reageert er niet op), maar Esc sluit het menu en
        // zet de focus terug op de knop (SHELL-L03-1, #2822): de documentlistener hoort Esc hier niet meer.
        e.stopPropagation();
        if (e.key === "Escape" && open) {
          e.preventDefault();
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}
    >
      {trigger ? (
        <span
          ref={setTriggerRef}
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={label}
          className={`pane-dropmenu__trigger${open ? " is-open" : ""}`}
          onClick={() => setOpen(!open)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen(!open);
            }
          }}
        >
          {trigger(open)}
        </span>
      ) : (
        <button
          ref={setTriggerRef}
          type="button"
          className={`pane-icon-btn${open ? " is-active" : ""}`}
          onClick={() => setOpen(!open)}
          title={label}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <circle cx="3" cy="8" r="1.4" fill="currentColor" />
            <circle cx="8" cy="8" r="1.4" fill="currentColor" />
            <circle cx="13" cy="8" r="1.4" fill="currentColor" />
          </svg>
        </button>
      )}
      {open ? (
        <div
          ref={menuRef}
          className={`pane-header__menu pane-dropmenu__menu${align === "left" ? " is-left" : ""}`}
          role="menu"
          aria-label={label}
        >
          {head ? <div className="pane-menu__head">{head}</div> : null}
          {items.map((item, i) => (
            <React.Fragment key={i}>
              {item.separator ? <div className="pane-header__menusep" role="separator" /> : null}
              {confirming === i && item.confirm ? (
                <div className="pane-dropmenu__confirm">
                  <span>{item.label}</span>
                  <button
                    type="button"
                    className="pane-btn pane-dropmenu__confirm-yes"
                    onClick={() => choose(item, i)}
                  >
                    {item.confirm.label}
                  </button>
                  <button type="button" className="pane-btn" onClick={() => setConfirming(null)}>
                    {item.confirm.cancel}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  role={item.checked !== undefined ? "menuitemcheckbox" : "menuitem"}
                  aria-checked={item.checked}
                  className={`pane-header__menuitem${item.checked ? " is-checked" : ""}${item.danger ? " is-danger" : ""}`}
                  disabled={item.disabled}
                  title={item.title}
                  onClick={() => choose(item, i)}
                >
                  {item.checked !== undefined ? (
                    <span className="pane-header__check" aria-hidden="true">
                      {item.checked ? "✓" : ""}
                    </span>
                  ) : null}
                  {item.icon ? <span className="pane-dropmenu__icon">{item.icon}</span> : null}
                  <span className="pane-dropmenu__label">{item.label}</span>
                </button>
              )}
            </React.Fragment>
          ))}
          {foot ? <div className="pane-menu__foot">{foot}</div> : null}
        </div>
      ) : null}
    </span>
  );
}
